import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Heart, Target, Users, BookOpen, ShieldCheck, Lock, Mail, CreditCard, Trash2 } from "lucide-react";
import SEO from "../components/SEO";
import EventCalendar from "../components/EventCalendar";
import Leaderboard from "../components/Leaderboard";
import VolunteerForm from "../components/VolunteerForm";
import { Editable } from "../components/Editable";
import { useContentApi } from "../context/ContentContext";
import { PUBLIC_DISCLAIMER_TITLE, PublicDisclaimer } from "../components/legal/HoldHarmless";
import api from "../lib/api";

// Tiny markdown helper — only renders **bold** safely (no full markdown engine
// needed for our short disclosure copy).
const renderInlineMarkdown = (text) => {
    if (!text) return null;
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => (
        p.startsWith("**") && p.endsWith("**")
            ? <strong key={i}>{p.slice(2, -2)}</strong>
            : <React.Fragment key={i}>{p}</React.Fragment>
    ));
};

const About = () => {
    const { c } = useContentApi();
    const location = useLocation();
    const [disclosures, setDisclosures] = useState([]);
    useEffect(() => {
        api.get("/disclosures").then(({ data }) => setDisclosures(data || [])).catch(() => setDisclosures([]));
    }, []);
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.slice(1);
            // wait one tick for layout
            requestAnimationFrame(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }, [location.hash]);
    const values = [
        { icon: Target, titleKey: "about.value1_title", bodyKey: "about.value1_body" },
        { icon: Users, titleKey: "about.value2_title", bodyKey: "about.value2_body" },
        { icon: BookOpen, titleKey: "about.value3_title", bodyKey: "about.value3_body" },
        { icon: Heart, titleKey: "about.value4_title", bodyKey: "about.value4_body" },
    ];
    return (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="about-page">
            <SEO
                title="About · Mission, governance, transparency"
                description="Reddog Community Foundation is an independent nonprofit corporation governed by a community board with full public-ledger transparency and dual-signoff spending controls. Read our mission, governance, and disclosures."
                path="/about"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    name: "About Reddog Community Foundation",
                    description:
                        "Mission, governance, and transparency disclosures for Reddog Community Foundation Inc., an independent nonprofit corporation in Frenchtown, Montana.",
                }}
            />
            <Editable contentKey="about.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                Our story
            </Editable>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-8">
                <Editable contentKey="about.title1" as="span">Built by parents,</Editable>
                <br />
                <Editable contentKey="about.title2" as="span" className="text-[#E53935]">
                    for our students.
                </Editable>
            </h1>
            <Editable
                contentKey="about.body"
                as="p"
                multiline
                className="text-lg text-[#4A5568] leading-relaxed mb-12 max-w-3xl"
            >
                Reddog Community Foundation Inc. supports schools, families, and community needs through restricted fundraising, responsible oversight, volunteer expertise, and public accountability.
            </Editable>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
                {values.map((v) => (
                    <div key={v.titleKey} className="bg-white border border-black/5 rounded-2xl p-8" data-testid={`about-value-${v.titleKey}`}>
                        <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center mb-5">
                            <v.icon className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                        </div>
                        <Editable contentKey={v.titleKey} as="h3" className="font-heading text-xl text-[#1C252E] font-medium mb-2">
                            {c(v.titleKey, "Title")}
                        </Editable>
                        <Editable contentKey={v.bodyKey} as="p" multiline className="text-sm text-[#4A5568] leading-relaxed">
                            {c(v.bodyKey, "Body")}
                        </Editable>
                    </div>
                ))}
            </div>

            {/* ----------- Privacy at a glance ----------- */}
            <Link
                to="/privacy"
                data-testid="about-privacy-at-a-glance"
                className="group block bg-white border border-black/10 rounded-3xl p-8 lg:p-10 mb-12 hover:border-[#E53935] transition-colors"
            >
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#F2D8D5] flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-[#E53935]" strokeWidth={1.75} />
                        </div>
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium">
                            Privacy at a glance
                        </h2>
                    </div>
                    <span className="text-xs font-medium text-[#E53935] group-hover:underline">
                        Read the full privacy policy →
                    </span>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm text-[#4A5568]">
                    <li className="flex items-start gap-3">
                        <Lock className="w-4 h-4 mt-0.5 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                        <span><strong className="text-[#1C252E] font-medium">Encrypted in transit</strong> — every page and form uses HTTPS.</span>
                    </li>
                    <li className="flex items-start gap-3">
                        <ShieldCheck className="w-4 h-4 mt-0.5 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                        <span><strong className="text-[#1C252E] font-medium">We never sell your data.</strong> No ad networks, no resale, ever.</span>
                    </li>
                    <li className="flex items-start gap-3">
                        <Mail className="w-4 h-4 mt-0.5 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                        <span><strong className="text-[#1C252E] font-medium">Magic-link sign-in.</strong> Donors don't need a password.</span>
                    </li>
                    <li className="flex items-start gap-3">
                        <CreditCard className="w-4 h-4 mt-0.5 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                        <span><strong className="text-[#1C252E] font-medium">No card data stored.</strong> Payments are handled by PCI-compliant processors.</span>
                    </li>
                    <li className="flex items-start gap-3 sm:col-span-2">
                        <Trash2 className="w-4 h-4 mt-0.5 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                        <span><strong className="text-[#1C252E] font-medium">Delete on request.</strong> Email us and we'll remove your personal data within 30 days.</span>
                    </li>
                </ul>
            </Link>

            <div className="bg-[#F4F1EA] rounded-3xl p-10 lg:p-14">
                <Editable contentKey="about.flow_title" as="h2" className="font-heading text-2xl sm:text-3xl text-[#1C252E] font-medium mb-4">
                    How your support flows
                </Editable>
                <ul className="space-y-3 text-[#4A5568]">
                    <li><Editable contentKey="about.flow_l1" as="span">55% classroom supplies & enrichment</Editable></li>
                    <li><Editable contentKey="about.flow_l2" as="span">25% field trips & scholarships</Editable></li>
                    <li><Editable contentKey="about.flow_l3" as="span">15% family support programs</Editable></li>
                    <li><Editable contentKey="about.flow_l4" as="span">5% school events & teacher appreciation</Editable></li>
                </ul>
            </div>

            <EventCalendar />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
                <Leaderboard limit={5} compact={false} />
                <div className="bg-white border border-black/5 rounded-3xl p-8" data-testid="about-volunteer-wrap">
                    <VolunteerForm />
                </div>
            </div>

            {/* ----------- Get involved CTAs ----------- */}
            <div id="get-involved" className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 scroll-mt-24" data-testid="about-get-involved">
                <Link to="/apply/contractor" data-testid="about-apply-contractor-cta" className="group bg-white border border-black/10 rounded-3xl p-8 hover:border-[#E53935] transition-colors block">
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2">For trades &amp; service providers</p>
                    <h3 className="font-heading text-2xl text-[#1C252E] font-medium mb-2 group-hover:text-[#E53935] transition-colors">Apply to be a vetted contractor →</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">Submit your business, license, and insurance details to be considered for paid work funded by approved foundation projects. No guarantee of work; all selections are reviewed under our conflict-of-interest policy.</p>
                </Link>
                <Link to="/apply/grant" data-testid="about-apply-grant-cta" className="group bg-white border border-black/10 rounded-3xl p-8 hover:border-[#E53935] transition-colors block">
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2">For teachers &amp; community groups</p>
                    <h3 className="font-heading text-2xl text-[#1C252E] font-medium mb-2 group-hover:text-[#E53935] transition-colors">Apply for a micro-grant →</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">Request funding for a specific classroom, family, or community project. Approved micro-grants are tracked publicly on the ledger so the community can see the impact.</p>
                </Link>
            </div>

            {/* ----------- Required disclosures (CMS-managed) ----------- */}
            <div id="governance" className="mt-16 space-y-5 scroll-mt-24" data-testid="about-disclosures">
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mb-2">
                    <h2 className="font-heading text-2xl sm:text-3xl text-[#1C252E] font-medium">Governance &amp; disclosures</h2>
                    <Link
                        to="/legal/changes"
                        data-testid="about-legal-changes-link"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#E53935] hover:underline"
                    >
                        See every edit to our legal documents →
                    </Link>
                </div>

                {disclosures.length === 0 ? (
                    <p className="text-sm text-[#4A5568] italic">Disclosures unavailable right now.</p>
                ) : disclosures.map((d) => {
                    const bg = d.kind === "amber" ? "bg-amber-50 border-amber-200" : "bg-white border-black/10";
                    const labelColor = d.kind === "amber" ? "text-amber-800" : "text-[#1C252E]";
                    const isDisclaimer = d.slug === "responsibility-disclaimer";
                    return (
                        <div
                            key={d.id}
                            id={d.slug}
                            data-testid={`about-disclosure-${d.slug}`}
                            className={`${bg} border rounded-2xl p-6 scroll-mt-24`}
                        >
                            <p className={`text-xs uppercase tracking-wider font-bold ${labelColor} mb-2`}>
                                {isDisclaimer ? PUBLIC_DISCLAIMER_TITLE : d.title}
                            </p>
                            {d.image_url && !isDisclaimer && (
                                <img src={d.image_url} alt="" className="w-full max-h-56 object-cover rounded-xl mb-3 bg-[#F4F1EA] border border-black/5" loading="lazy" />
                            )}
                            {isDisclaimer ? (
                                <PublicDisclaimer />
                            ) : (
                                <p className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-line">
                                    {renderInlineMarkdown(d.body)}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default About;
